import { useEffect, useState } from 'react'

import { BeatTheProEntryStateEnum } from '../../__generated__/hooks'
import BottomSheet from '../../components/BottomSheet'
import { IconCheck } from '../../components/Icon'

/**
 * The pro's "decide this bet" sheet. Won / Lost are one-tap direct commits
 * (the pro is moving fast hole-by-hole); Delete is destructive so it gets an
 * inline confirm. If the bet is already decided, that outcome is pre-selected
 * so the pro sees current state and can correct it.
 */
const SetResultSheet = ({
  open,
  playerName,
  betLabel,
  currentState,
  saving,
  onClose,
  onSetResult,
  onDelete,
}: {
  open: boolean
  playerName: string
  betLabel: string
  currentState: BeatTheProEntryStateEnum
  saving: boolean
  onClose: () => void
  onSetResult: (state: BeatTheProEntryStateEnum) => void
  onDelete: () => void
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false)
  useEffect(() => {
    if (open) setConfirmDelete(false)
  }, [open])

  const isWon = currentState === BeatTheProEntryStateEnum.Won
  const isLost = currentState === BeatTheProEntryStateEnum.Lost

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      ariaLabel="Avgjør bet"
      backdropClassName="bg-ink-900/45 backdrop-blur-sm"
      panelClassName="max-w-xl bg-paper"
      showGrabHandle
    >
      <div
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}
      >
        <div className="px-6 pt-3 pb-3">
          <h2 className="font-display font-extrabold text-lg text-green-900">
            Avgjør bet
          </h2>
          <p className="text-sm text-ink-700 mt-0.5 truncate">{playerName}</p>
          <p className="text-xs text-ink-500 font-mono num mt-0.5">
            {betLabel}
          </p>
        </div>

        <div className="px-6 flex flex-col gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={() => onSetResult(BeatTheProEntryStateEnum.Won)}
            className={`w-full py-3 rounded-xl font-display font-extrabold text-base bg-green-700 text-paper flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
              isWon ? 'ring-2 ring-green-900' : ''
            }`}
          >
            <IconCheck className="w-5 h-5" />
            Slo proen (Seier)
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => onSetResult(BeatTheProEntryStateEnum.Lost)}
            className={`w-full py-3 rounded-xl font-display font-extrabold text-base bg-flag text-paper transition-colors disabled:opacity-50 ${
              isLost ? 'ring-2 ring-flag-dark' : ''
            }`}
          >
            Tapte mot proen
          </button>

          {confirmDelete ? (
            <div className="flex gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={onDelete}
                className="flex-1 py-3 rounded-xl font-display font-extrabold text-base bg-flag text-paper transition-colors disabled:opacity-50"
              >
                Slett, ja
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="flex-1 py-3 rounded-xl font-semibold text-sm bg-cream-card text-ink-700 hover:bg-ink-100 transition-colors"
              >
                Avbryt
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="w-full py-3 rounded-xl font-semibold text-base bg-paper text-flag border border-flag-dark hover:bg-flag/5 transition-colors"
            >
              Slett bet
            </button>
          )}
        </div>

        <div className="px-6 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-xl font-semibold text-sm bg-cream-card text-ink-700 hover:bg-ink-100 transition-colors"
          >
            Lukk
          </button>
        </div>
      </div>
    </BottomSheet>
  )
}

export default SetResultSheet
